import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import LeaveCalendar from "../components/employee/LeaveCalendar";
import ViewOneEmployeeRecord from "../components/hr/ViewOneEmployeeRecord";
import ViewOneEmpPerm from "../components/ViewOneEmpPerm";
import { Card, Row, Col, Avatar, Typography, Descriptions, Tag, Divider, Table, Button, Upload, Modal, Form, Input, Select, DatePicker, message, InputNumber, Space, Spin, Badge } from "antd";
import {
    ArrowLeftOutlined, EditOutlined, CameraOutlined, DownloadOutlined, SaveOutlined, CloseOutlined, PlusOutlined, DeleteOutlined, CalendarOutlined,
    LogoutOutlined,
    SafetyCertificateOutlined,
    PhoneOutlined,
    MobileOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";


const { Text } = Typography;

export default function ViewEmpDetails({ employee }) {
    const { authData, backUrl } = useContext(AuthContext)

    const emp = employee?.employee ?? employee;

    const [showCalendar, setShowCalendar] = useState(false);
    const [showLeaves, setShowLeaves] = useState(false);
    const [showPermissions, setShowPermissions] = useState(false);
    const [showOnDuty, setShowOnDuty] = useState(false);

    const [onDutyRecords, setOnDutyRecords] = useState([]);
    const [onDutyLoading, setOnDutyLoading] = useState(false);
    const [onDutyStatusFilter, setOnDutyStatusFilter] = useState("ALL");

    const closeCalendar = () => setShowCalendar(false);
    const closeLeaves = () => setShowLeaves(false);
    const closePermissions = () => setShowPermissions(false);
    const closeOnDuty = () => setShowOnDuty(false);

    const fetchOnDutyRecords = async () => {
        if (!emp?.id) return;
        setOnDutyLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/hr/onduty/?employee=${emp.id}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setOnDutyRecords(res.data.results || res.data || []);
        } catch (err) {
            console.error("Failed to fetch on-duty records", err);
            setOnDutyRecords([]);
        } finally {
            setOnDutyLoading(false);
        }
    };

    const handleShowOnDuty = () => {
        setShowOnDuty(true);
        fetchOnDutyRecords();
    };

    const filteredOnDutyRecords = onDutyStatusFilter === "ALL"
        ? onDutyRecords
        : onDutyRecords.filter(r => (r.status || "").toUpperCase() === onDutyStatusFilter);

    const statusColor = {
        APPROVED: "success",
        PENDING: "warning",
        REJECTED: "error",
    };

    const onDutyColumns = [
        {
            title: "Date",
            key: "date",
            render: (_, r) => r.start_time ? dayjs(r.start_time).format("DD MMM YYYY") : "-",
            width: 110,
        },
        {
            title: "Type",
            dataIndex: "onduty_type",
            key: "onduty_type",
            render: v => v || "-",
            width: 130,
        },
        {
            title: "Start",
            dataIndex: "start_time",
            key: "start_time",
            render: v => v ? dayjs(v).format("HH:mm") : "-",
            width: 70,
        },
        {
            title: "End",
            dataIndex: "end_time",
            key: "end_time",
            render: v => v ? dayjs(v).format("HH:mm") : "-",
            width: 70,
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            render: v => v || "-",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: v => (
                <Badge status={statusColor[v?.toUpperCase()] || "default"} text={v || "-"} />
            ),
            width: 100,
        },
    ];

    return (
        <>
            {/* Contact Info Row */}
            <div style={{
                display: "flex",
                gap: "16px",
                marginTop: "8px",
                marginBottom: "4px",
                flexWrap: "wrap",
                padding: "10px 12px",
                background: "#f6f8fb",
                borderRadius: "8px",
                border: "1px solid #e8edf3"
            }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 13 }}>
                    <MobileOutlined style={{ color: "#0e497b" }} />
                    <Text type="secondary" style={{ fontWeight: 500 }}>Personal:</Text>
                    <Text strong>{emp?.mobile_number || "-"}</Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: 13 }}>
                    <PhoneOutlined style={{ color: "#0e497b" }} />
                    <Text type="secondary" style={{ fontWeight: 500 }}>CUG:</Text>
                    <Text strong>{emp?.official_cug_number || "-"}</Text>
                </div>
            </div>

            {/* Action Buttons */}
            <div
                style={{
                    display: "flex",
                    gap: "10px",
                    marginTop: "10px",
                    flexWrap: "wrap",
                    justifyContent: 'space-around',
                    width: '600px'
                }}
            >
                {/* Show Calendar */}
                <Button
                    type="primary"
                    icon={<CalendarOutlined />}
                    onClick={() => setShowCalendar(true)}
                    style={{ backgroundColor: '#0e497b', color: 'white' }}
                >
                    Calendar
                </Button>

                {/* Leaves */}
                <Button
                    type="default"
                    icon={<LogoutOutlined />}
                    onClick={() => setShowLeaves(true)}
                    style={{ backgroundColor: '#0e497b', color: 'white' }}

                >
                    Leaves
                </Button>

                {/* Permissions */}
                <Button
                    type="dashed"
                    icon={<SafetyCertificateOutlined />}
                    onClick={() => setShowPermissions(true)}
                    style={{ backgroundColor: '#0e497b', color: 'white' }}

                >
                    Permissions
                </Button>

                {/* On-Duty Records */}
                <Button
                    type="default"
                    icon={<ClockCircleOutlined />}
                    onClick={handleShowOnDuty}
                    style={{ backgroundColor: '#0e497b', color: 'white' }}
                >
                    On-Duty
                </Button>
            </div>

            <Modal
                title="Calendar"
                open={showCalendar}          // 👈 AntD v5 uses "open"
                onCancel={closeCalendar}
                footer={null}
                width={600}
            >
                <LeaveCalendar employee={emp} />
            </Modal>

            <Modal
                title="Employee Leaves"
                open={showLeaves}
                onCancel={closeLeaves}
                footer={null}
                width={1200}
            >
                <ViewOneEmployeeRecord
                    employee={{
                        id: emp.id,
                        employee: emp, // Wrap the employee data
                        leaves_taken_breakdown: {
                            sick_leave: emp.taken_sick_leave || 0,
                            casual_leave: emp.taken_casual_leave || 0,
                            earned_leave: emp.taken_earned_leave || 0,
                            lop_leave: emp.taken_lop_leave || 0
                        }
                    }}
                    isProfile={false}
                />
            </Modal>

            <Modal
                title="Employee Permissions"
                open={showPermissions}          // 👈 AntD v5 uses "open"
                onCancel={closePermissions}
                footer={null}
                width={1200}
            >
                <ViewOneEmpPerm employee={{
                    id: emp.id,
                    employee: emp, // Wrap the employee data
                    leaves_taken_breakdown: {
                        sick_leave: emp.taken_sick_leave || 0,
                        casual_leave: emp.taken_casual_leave || 0,
                        earned_leave: emp.taken_earned_leave || 0,
                        lop_leave: emp.taken_lop_leave || 0
                    }
                }}
                    isProfile={false} />
            </Modal>

            {/* On-Duty Records Modal */}
            <Modal
                title={`On-Duty Records — ${emp?.first_name || ""} ${emp?.last_name || ""}`}
                open={showOnDuty}
                onCancel={closeOnDuty}
                footer={null}
                width={820}
            >
                {/* Status filter */}
                <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                    {["ALL", "PENDING", "APPROVED", "REJECTED"].map(s => (
                        <Button
                            key={s}
                            size="small"
                            type={onDutyStatusFilter === s ? "primary" : "default"}
                            onClick={() => setOnDutyStatusFilter(s)}
                            style={onDutyStatusFilter === s ? { backgroundColor: "#0e497b", borderColor: "#0e497b" } : {}}
                        >
                            {s === "ALL" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
                            {s !== "ALL" && (
                                <span style={{ marginLeft: 4, fontWeight: 600 }}>
                                    ({onDutyRecords.filter(r => (r.status || "").toUpperCase() === s).length})
                                </span>
                            )}
                        </Button>
                    ))}
                    <Button
                        size="small"
                        icon={<ClockCircleOutlined />}
                        onClick={fetchOnDutyRecords}
                        loading={onDutyLoading}
                        style={{ marginLeft: "auto" }}
                    >
                        Refresh
                    </Button>
                </div>

                {onDutyLoading ? (
                    <div style={{ textAlign: "center", padding: 32 }}><Spin /></div>
                ) : filteredOnDutyRecords.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#999", padding: 32 }}>
                        No on-duty records found.
                    </div>
                ) : (
                    <Table
                        dataSource={filteredOnDutyRecords}
                        columns={onDutyColumns}
                        rowKey="id"
                        size="small"
                        pagination={{ pageSize: 8, showSizeChanger: false }}
                        scroll={{ x: 600 }}
                    />
                )}
            </Modal>

        </>
    )
}